<template>
    <!-- An outer container is needed so that padding can be applied without breaking the "collapsed" state -->
    <!-- as padding applied to a outer container with a height of zero will still display, per the CSS spec -->
    <flex-element
        :element-type="elementType"
        class="container f-overflow-hidden"
        :class="stateClass"
        class-default-inline="f-absolute f-bg-white"
        :style="containerStyles"
        :exclude-classes="dropZoneClassGroups"
        @mouseenter.native="onMouseEnter"
        @mouseleave.native="onMouseLeave"
    >
        <div
            role="menu"
            class="f-flex-wrap"
            :class="dropZoneClasses"
            :style="contentStyles"
        >
            <drop-zone-output
                :elements-new="elementsNew"
                element-type="drop-zone-output"
                @element-updated="elementUpdated"
                @delete="setHeight"
            />

            <drop-zone-input
                :elements-new="elementsNew"
                element-type="drop-zone-output"
                :force-display="true"
                is-structural-component
                @drop="onDrop"
            />
        </div>
    </flex-element>
</template>

<script>
import baseOrganism from '@/mixins/base-organism/base-organism-smart';
import { getClassDefinition } from '@/utils/tailwind-helper';
import { STATE_CLASSES } from '@/data/constants/classes';

export default {
    name: 'nav-dropdown-content',
    mixins: [baseOrganism],

    props: {
        displayName: {
            type: String,
            default: 'Nav Dropdown Content',
        },

        direction: {
            type: String,
            required: true,
        },

        isExpanded: {
            type: Boolean,
            required: true,
        },

        isFullWidth: {
            type: Boolean,
            default: false,
        },

        isMobile: {
            type: Boolean,
            default: false,
        },

        speed: {
            type: String,
            required: true,
        },
    },

    data() {
        return {
            borderSize: 0,
            cssDisplay: 'block',
            dropZoneClassGroups: ['alignItems', 'flexDirection', 'justifyContent', 'padding'],
            height: 0,
        };
    },

    computed: {
        dropZoneClasses() {
            return this.htmlClass.filter((className) => {
                const { tailwindDefinitionProp } = getClassDefinition(className);

                return this.dropZoneClassGroups.includes(tailwindDefinitionProp);
            });
        },

        containerStyles() {
            this.setBorderSize();

            const fullWidthStyles = {
                position: 'fixed',
                top: '100%',
                left: '0',
                width: '100%',
            };

            switch (true) {
                case this.direction === 'down' && this.isExpanded: {
                    return {
                        transitionProperty: 'height, box-shadow, border-width',
                        transitionDuration: `${ this.speed }s`,
                        height: `${ this.height }px`,
                        top: `calc(100% + ${ this.borderSize }px`,
                        left: '0',
                        ...(this.isFullWidth ? fullWidthStyles : null),
                    };
                }

                case this.direction === 'down' && !this.isExpanded:
                    return {
                        transitionProperty: 'height, box-shadow, border-width',
                        transitionDuration: `${ this.speed }s`,
                        height: '0',
                        top: `calc(100% + ${ this.borderSize }px`,
                        left: '0',
                        boxShadow: '0 0 0 0 transparent',
                        borderWidth: '0',
                        ...(this.isFullWidth ? fullWidthStyles : null),
                    };

                case this.direction === 'right' && this.isExpanded: {
                    const width = this.$el ? this.$el.scrollWidth : 0;

                    return {
                        transitionProperty: 'width, box-shadow, border-width',
                        transitionDuration: `${ this.speed }s`,
                        width: `${ width }px`,
                        top: '0',
                        left: `calc(100% + ${ this.borderSize })`,
                    };
                }

                case this.direction === 'right' && !this.isExpanded:
                    return {
                        transitionProperty: 'width, box-shadow, border-width',
                        transitionDuration: `${ this.speed }s`,
                        width: '0',
                        top: '0',
                        left: `calc(100% + ${ this.borderSize })`,
                        boxShadow: '0 0 0 0 transparent',
                        borderWidth: '0',
                    };

                case this.direction === 'left' && this.isExpanded: {
                    const width = this.$el ? this.$el.scrollWidth : 0;

                    return {
                        transitionProperty: 'width, box-shadow, border-width',
                        transitionDuration: `${ this.speed }s`,
                        width: `${ width }px`,
                        top: '0',
                        right: '100%',
                    };
                }

                case this.direction === 'left' && !this.isExpanded:
                    return {
                        transitionProperty: 'width, box-shadow, border-width',
                        transitionDuration: `${ this.speed }s`,
                        width: '0',
                        top: '0',
                        right: '100%',
                        boxShadow: '0 0 0 0 transparent',
                        borderWidth: '0',
                    };

                default:
                    console.warn('Invalid nav item state detected.');
                    return null;
            }
        },

        contentStyles() {
            return {
                display: this.isExpanded ? 'flex' : 'none',
            };
        },

        length() {
            return this.elementsNew ? this.elementsNew.length : 0;
        },

        stateClass() {
            return this.isMobile ? STATE_CLASSES.MOBILE : '';
        },
    },

    watch: {
        async isExpanded() {
            await this.$nextTick();
            this.setHeight();
        },

        length: {
            handler() {
                this.$emit('count', this.length);
            },
            immediate: true,
        },
    },

    mounted() {
        this.setHeight();
    },

    methods: {
        onMouseEnter() {
            this.$emit('mouse-enter');
        },

        onMouseLeave() {
            this.$emit('mouse-leave');
        },

        onDrop() {
            // we need to set a timeout to give time for the element to load
            // waiting for nextTick won't work for certain elements that are still "loading" after mounted() is fired
            // ... e.g. image elements
            window.setTimeout(this.setHeight, 500);
        },

        async setHeight() {
            await this.$nextTick();
            this.height = this.$el.scrollHeight;
        },

        setBorderSize() {
            if (!this.$el) return;

            const borderProperty = this.direction === 'down' ? 'border-bottom-width' : `border-${ this.direction }-width`;
            const borderSize = +window.getComputedStyle(this.$el.parentNode)[borderProperty].slice(0, -2);

            this.borderSize = borderSize;
        },
    },
};
</script>

<style lang="scss" scoped>
.container {
    &.#{ $is-mobile } {
        position: relative !important;
    }
}
</style>
